/**
 * Recurring (TMMAPP-0009, design §14.4 row 4; TMMAPP-0145 moved the cash flow to its own page,
 * `CashFlowPage`): the bills and income you expect, repeating or one-time. Two tabs (TMMAPP-0159
 * §F 28): **Items to review**, a subscriber's suggested recurring bills from the imported history
 * (TMMAPP-0141 §B), and **Recurring transactions**, the series list or calendar with the account
 * filter. The all-accounts "Tidy up your history" queue isn't here: it lives on each account's page.
 * A link from Cash flow may carry a series to expand.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useNotify } from '@/shell/NotifyProvider';
import { accountsApi, type Account } from '@/api/accounts';
import { ErrorCodes, isApiError } from '@/api/errors';
import { plannedApi, type Occurrence, type Series, type SeriesStatus } from '@/api/planned';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SuggestedRecurringBills } from '@/features/genesis/SuggestedRecurringBills';
import { useGenesisProposals } from '@/features/genesis/useGenesisProposals';
import { ReviewPanel, ReviewTabLoading, ReviewTabs } from '@/components/ReviewTabs';
import { useReviewTab, type ReviewCount } from '@/components/reviewTab';
import { ErrorState } from '@/components/ErrorState';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { toCivil } from '@/features/envelope/window';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { HeadedCard } from '@/components/HeadedCard';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import {
  civilMonthParam,
  enumParam,
  optionalEnumParam,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import { addCivilDays, readWindow, useAppToday } from '@/shared/servedWindow';
import { calendarWindow } from './calendarDates';
import { OccurrenceCalendar } from './OccurrenceCalendar';
import { OccurrenceSheet } from './OccurrenceSheet';
import { plannedErrorKey } from './plannedErrors';
import { seriesIdFromState } from './plannedRoutes';
import { SeriesEditorDialog, type EditorMode } from './SeriesEditorDialog';
import { SeriesList } from './SeriesList';

type StatusFilter = SeriesStatus | 'ALL';
type PlannedView = 'calendar' | 'list';
type PlannedTab = 'review' | 'transactions';

const STATUS_FILTERS: readonly StatusFilter[] = ['ACTIVE', 'ENDED', 'CANCELLED', 'ALL'];
const URL_SCHEMA = {
  // The prototype's rows are the default view (TMMAPP-0151 §H 23); the calendar is a toggle away.
  view: enumParam<PlannedView>(['calendar', 'list'], 'list'),
  status: enumParam<StatusFilter>(STATUS_FILTERS, 'ACTIVE'),
  // The account filter: `all` or an account id. The page checks it against the loaded accounts.
  account: stringParam('all'),
  // Absent: the default by the Items to review count (TMMAPP-0159 §F 27); a chosen tab is kept.
  tab: optionalEnumParam<PlannedTab>(['review', 'transactions']),
};
const REVIEW_PANEL_ID = 'recurring-review';

const monthOf = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

function editableAccounts(list: Account[]): Account[] {
  return list.filter((a) => isOwnerAccount(a) || a.myPermissions.canEdit);
}

function PlannedPageInner() {
  const { t } = useTranslation();
  const me = useMe();
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  // The calendar month and the account are view state (TMM-B6): `month` (omitted for the current
  // month) and `account` (omitted for all accounts), replaced, never pushed.
  const thisMonth = monthOf(new Date());
  const schema = useMemo(() => ({ ...URL_SCHEMA, month: civilMonthParam(thisMonth) }), [thisMonth]);
  const [{ view, status: statusFilter, account: accountRaw, month, tab: requestedTab }, setUrl] =
    useUrlState(schema);
  const location = useLocation();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const accountId = /^\d+$/.test(accountRaw) ? Number(accountRaw) : 'all';
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7)) - 1;
  const [occurrences, setOccurrences] = useState<Occurrence[]>([]);
  const [series, setSeries] = useState<Series[]>([]);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  // The month's calendar is its own section (TMMAPP-0159 §0).
  const [calendarFailure, setCalendarFailure] = useState<{ error: unknown } | null>(null);
  // The first day the month's calendar was served from (TMMAPI-0188 U3); null when not served.
  const [calendarFrom, setCalendarFrom] = useState<string | null>(null);
  const notify = useNotify();
  const [openOcc, setOpenOcc] = useState<Occurrence | null>(null);
  const [editor, setEditor] = useState<{
    mode: EditorMode;
    series?: Series;
    fromDate?: string;
    startOn?: string;
  } | null>(null);
  const [endFrom, setEndFrom] = useState<{ series: Series; date: string } | null>(null);
  const [cancelSeries, setCancelSeries] = useState<Series | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  // A series opened from Cash flow (or an envelope's forecast) arrives expanded.
  const [expandedId, setExpandedId] = useState<number | null>(() =>
    seriesIdFromState(location.state),
  );
  // That series, until the first load shows whether the status filter holds it (TMMAPP-0146 item 1).
  const linkedSeries = useRef(expandedId);
  const [subscribe, setSubscribe] = useState(false);
  const [seriesOpen, setSeriesOpen] = useState(true);
  const guest = me.tier === 'GUEST';
  const subscriber = me.tier === 'SUBSCRIBER';
  const createSeries = useSubscriberAction(() => setEditor({ mode: 'create' }));
  // Items to review is the suggested bills (a subscriber's own); its count is their served list.
  const bills = useGenesisProposals('series', subscriber);
  const billsCount: ReviewCount =
    bills.status === 'ready'
      ? bills.proposals.length
      : bills.status === 'error'
        ? 'unavailable'
        : 'loading';
  // A link with the series list's own view state (a status, an account, a month, the calendar) is a
  // link to Recurring transactions: it wins over the default like a `?tab=` would.
  const transactionsLink =
    view !== 'list' || statusFilter !== 'ACTIVE' || accountRaw !== 'all' || month !== thisMonth;
  const tab = useReviewTab<'transactions'>(
    requestedTab ?? (transactionsLink ? 'transactions' : null),
    billsCount,
    'transactions',
    subscriber,
  );
  const showTab = (next: PlannedTab) => setUrl({ tab: next }, { push: true });

  const title = usePageTitle({ title: t('toolsNav.recurring') });

  const span = calendarWindow(year, monthIndex);
  const today = useAppToday();
  const accountFilter = accountId === 'all' ? undefined : accountId;
  const fetchKey = `${guest}|${span.from}|${span.to}|${today}|${accountFilter ?? 'all'}|${statusFilter}`;
  const lastFetch = useRef<string | null>(null);

  const load = useCallback(async () => {
    // The month's calendar by the one window rule (TMMAPP-0159 §0): no past `from`, nothing asked
    // for a month already over, one retry on a 422. It settles on its own, so a calendar failure
    // never blanks the series list or the suggested bills.
    const [listed, calendar] = await Promise.allSettled([
      Promise.all([
        accountsApi.list(),
        plannedApi.listSeries({
          accountId: accountFilter,
          status: statusFilter === 'ALL' ? undefined : statusFilter,
        }),
      ]),
      readWindow(
        { from: span.from, to: span.to },
        today,
        (query) => plannedApi.calendarWindowed({ ...query, accountId: accountFilter }),
        { occurrences: [] as Occurrence[], windowFrom: null, windowTo: null },
      ),
    ]);
    if (calendar.status === 'fulfilled') {
      setOccurrences(calendar.value.occurrences);
      setCalendarFrom(calendar.value.windowFrom);
      setCalendarFailure(null);
    } else {
      setOccurrences([]);
      setCalendarFailure({ error: calendar.reason });
    }
    if (listed.status === 'rejected') {
      setLoadFailure({ error: listed.reason });
      return;
    }
    const [accts, ser] = listed.value;
    const editable = editableAccounts(accts);
    setAccounts(editable);
    // A link to an account that is gone (or not editable here) reads as every account.
    if (accountFilter !== undefined && !editable.some((a) => a.id === accountFilter)) {
      setUrl({ account: 'all' });
    }
    setSeries(ser);
    setLoadFailure(null);
    // A linked series the filter leaves out (an ended one, say) switches the filter to All, once.
    const linked = linkedSeries.current;
    linkedSeries.current = null;
    if (linked !== null && statusFilter !== 'ALL' && !ser.some((s) => s.id === linked)) {
      setUrl({ status: 'ALL' });
    }
  }, [span.from, span.to, today, accountFilter, statusFilter, setUrl]);

  useEffect(() => {
    if (guest) return;
    if (lastFetch.current === fetchKey) return;
    lastFetch.current = fetchKey;
    void load();
  }, [guest, fetchKey, load]);

  const currencyByAccount = useMemo(() => {
    const m: Record<number, string | null> = {};
    for (const a of accounts) m[a.id] = a.currency ?? a.operatingCurrency;
    return m;
  }, [accounts]);
  const accountNames = useMemo(() => {
    const m: Record<number, string> = {};
    for (const a of accounts) m[a.id] = a.name;
    return m;
  }, [accounts]);
  const defaultCurrency = me.user.currency;
  const seriesNames = useMemo(() => {
    const m: Record<number, string> = {};
    for (const s of series) m[s.id] = s.name;
    return m;
  }, [series]);
  const currencyBySeries = useMemo(() => {
    const m: Record<number, string | null> = {};
    for (const s of series) m[s.id] = currencyByAccount[s.accountId] ?? defaultCurrency;
    return m;
  }, [series, currencyByAccount, defaultCurrency]);
  const tomorrow = addCivilDays(toCivil(new Date()), 1);

  /** A confirmed series write: the dialog closes on success and keeps its error inline. */
  async function runConfirmed(fn: () => Promise<void>, okMessage: string, close: () => void) {
    if (confirmBusy) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await fn();
      close();
      notify(okMessage);
      await load();
    } catch (err) {
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        close();
        setSubscribe(true);
      } else {
        setConfirmError(t(plannedErrorKey(err) ?? 'planned.errorGeneric'));
      }
    } finally {
      setConfirmBusy(false);
    }
  }

  const openSeries =
    openOcc === null ? null : (series.find((s) => s.id === openOcc.seriesId) ?? null);
  const openAccount =
    openSeries === null ? null : (accounts.find((a) => a.id === openSeries.accountId) ?? null);

  if (guest) {
    return (
      <>
        <PageHeader
          title={title}
          subtitle={t('planned.subtitle')}
          actions={
            <>
              <Button
                variant="contained"
                aria-describedby={createSeries.descriptionId}
                onClick={createSeries.run}
              >
                {t('planned.newSeries')}
              </Button>
              <SubscriberActionHint
                id={createSeries.descriptionId}
                description={createSeries.description}
              />
            </>
          }
        />
        <Typography variant="body1">{t('planned.emptySeries')}</Typography>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={title}
        subtitle={t('planned.subtitle')}
        actions={
          <>
            <Button
              variant="contained"
              aria-describedby={createSeries.descriptionId}
              onClick={createSeries.run}
            >
              {t('planned.newSeries')}
            </Button>
            <SubscriberActionHint
              id={createSeries.descriptionId}
              description={createSeries.description}
            />
          </>
        }
      />
      {subscriber && (
        <ReviewTabs
          value={tab}
          count={billsCount}
          tabs={[{ value: 'transactions', label: t('planned.tabTransactions') }]}
          onChange={showTab}
          label={t('planned.tabsLabel')}
          panelId={REVIEW_PANEL_ID}
          sx={{ mb: 2 }}
        />
      )}
      {tab === null && <ReviewTabLoading />}
      {subscriber && (
        <ReviewPanel
          id={REVIEW_PANEL_ID}
          active={tab === 'review'}
          count={billsCount}
          mainLabel={t('planned.tabTransactions')}
          onMain={() => showTab('transactions')}
        >
          <SuggestedRecurringBills accounts={accounts} onAdded={() => void load()} source={bills} />
        </ReviewPanel>
      )}
      {tab === 'transactions' && (
        <>
          {loadFailure !== null && (
            <Box sx={{ mb: 2 }}>
              <ErrorState
                variant="section"
                error={loadFailure.error}
                what={t('errors.what.planned')}
                onRetry={() => void load()}
              />
            </Box>
          )}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ gap: 1.5, mb: 2, alignItems: { md: 'center' }, flexWrap: 'wrap' }}
          >
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="planned-account">{t('planned.account')}</InputLabel>
              <Select
                labelId="planned-account"
                label={t('planned.account')}
                value={accountId === 'all' ? 'all' : String(accountId)}
                onChange={(event) => setUrl({ account: event.target.value })}
              >
                <MenuItem value="all">{t('planned.allAccounts')}</MenuItem>
                {/* A linked account is an option before the list has loaded (no out-of-range value). */}
                {accountId !== 'all' && !accounts.some((a) => a.id === accountId) && (
                  <MenuItem value={String(accountId)} sx={{ display: 'none' }}>
                    {t('common.loading')}
                  </MenuItem>
                )}
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={String(a.id)}>
                    {a.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={view}
              onChange={(_event, value: PlannedView | null) => {
                if (value === null) return;
                setUrl({ view: value }, { push: true });
              }}
              aria-label={t('planned.viewCalendar')}
            >
              <ToggleButton value="calendar">{t('planned.viewCalendar')}</ToggleButton>
              <ToggleButton value="list">{t('planned.viewList')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {view === 'calendar' && (
            <HeadedCard title={t('planned.viewCalendar')} subtitle={t('planned.calendarSub')}>
              {calendarFailure !== null && (
                <Box sx={{ mb: 2 }}>
                  <ErrorState
                    variant="section"
                    error={calendarFailure.error}
                    what={t('errors.what.planned')}
                    onRetry={() => void load()}
                  />
                </Box>
              )}
              <OccurrenceCalendar
                year={year}
                monthIndex={monthIndex}
                occurrences={occurrences}
                defaultCurrency={defaultCurrency}
                currencyBySeries={currencyBySeries}
                seriesNames={seriesNames}
                subscriber={subscriber}
                onMonthChange={(y, m) =>
                  setUrl({ month: `${y}-${String(m + 1).padStart(2, '0')}` })
                }
                onSelect={setOpenOcc}
                onAddOnDay={(civil) => setEditor({ mode: 'create', startOn: civil })}
                fromToday={calendarFrom !== null && calendarFrom > span.from}
              />
            </HeadedCard>
          )}
          {lg && view === 'calendar' && (
            <Button sx={{ mt: 2 }} onClick={() => setSeriesOpen((v) => !v)}>
              {t('planned.viewList')}
            </Button>
          )}
          {(view === 'list' || lg) && (
            <Collapse in={view === 'list' || seriesOpen}>
              <Box sx={{ mt: 3 }}>
                <SeriesList
                  rows={series}
                  currencyByAccount={currencyByAccount}
                  accountNames={accountNames}
                  defaultCurrency={defaultCurrency}
                  callerName={me.user.displayName ?? ''}
                  subscriber={subscriber}
                  expandedId={expandedId}
                  onExpand={setExpandedId}
                  onEdit={(s) => setEditor({ mode: 'series', series: s })}
                  onCancel={(s) => {
                    setConfirmError(null);
                    setCancelSeries(s);
                  }}
                  onEndFrom={(s) => {
                    setConfirmError(null);
                    setEndFrom({ series: s, date: addCivilDays(toCivil(new Date()), 1) });
                  }}
                  onFilter={(next) => setUrl({ status: next })}
                  statusFilter={statusFilter}
                />
              </Box>
            </Collapse>
          )}
        </>
      )}

      {openOcc !== null && (
        <OccurrenceSheet
          occurrence={openOcc}
          series={openSeries}
          account={openAccount}
          owner={openAccount !== null && isOwnerAccount(openAccount)}
          currency={
            openAccount !== null
              ? (openAccount.currency ?? openAccount.operatingCurrency ?? defaultCurrency)
              : defaultCurrency
          }
          onClose={() => setOpenOcc(null)}
          onChanged={async () => {
            await load();
            if (openOcc !== null) {
              const tl = await plannedApi.timeline(openOcc.seriesId, {
                from: openOcc.originalOn,
                to: openOcc.originalOn,
              });
              const next =
                tl.find((o) => o.originalOn === openOcc.originalOn) ??
                occurrences.find(
                  (o) => o.seriesId === openOcc.seriesId && o.originalOn === openOcc.originalOn,
                );
              if (next) setOpenOcc(next);
            }
          }}
          // The sheet closes before the series editor opens: one modal at a time (DLG-005,
          // TMMAPP-0097 §C 21), as the series host does.
          onEditFollowing={() => {
            if (openSeries !== null && openOcc !== null) {
              const fromDate = openOcc.originalOn;
              setOpenOcc(null);
              setEditor({ mode: 'following', series: openSeries, fromDate });
            }
          }}
          onEditSeries={() => {
            if (openSeries !== null) {
              setOpenOcc(null);
              setEditor({ mode: 'series', series: openSeries });
            }
          }}
          // The sheet closes onto the List view with the series expanded, so something visible
          // happens below `lg` too (TMMAPP-0146 item 3). It pushes, like the view toggle, so Back
          // returns to the calendar (TMMAPP-0147 item 2).
          onOpenSeries={() => {
            if (openSeries === null) return;
            setOpenOcc(null);
            setExpandedId(openSeries.id);
            setUrl({ view: 'list' }, { push: true });
          }}
          onNotify={notify}
        />
      )}

      {editor !== null && (
        <SeriesEditorDialog
          mode={editor.mode}
          accounts={accounts}
          series={editor.series}
          fromDate={editor.fromDate}
          startOn={editor.startOn}
          onClose={() => setEditor(null)}
          onSaved={(message) => {
            notify(message);
            void load();
          }}
        />
      )}

      {endFrom !== null && (
        <ConfirmDialog
          title={t('planned.endFromSeriesTitle', { name: endFrom.series.name })}
          consequence={t('planned.endFromConsequence')}
          confirmLabel={t('planned.endSeries')}
          destructive
          busy={confirmBusy}
          error={confirmError}
          confirmDisabled={endFrom.date < tomorrow}
          onConfirm={() =>
            void runConfirmed(
              () => plannedApi.endFrom(endFrom.series.id, { fromDate: endFrom.date }),
              t('planned.seriesEnded'),
              () => setEndFrom(null),
            )
          }
          onCancel={() => setEndFrom(null)}
        >
          <TextField
            type="date"
            label={t('planned.endFromDate')}
            value={endFrom.date}
            onChange={(event) => setEndFrom({ ...endFrom, date: event.target.value })}
            disabled={confirmBusy}
            error={endFrom.date < tomorrow}
            helperText={endFrom.date < tomorrow ? t('planned.endFromPast') : undefined}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: tomorrow },
            }}
            fullWidth
          />
        </ConfirmDialog>
      )}

      {cancelSeries !== null && (
        <ConfirmDialog
          title={t('planned.cancelSeriesTitle', { name: cancelSeries.name })}
          consequence={t('planned.cancelSeriesConsequence')}
          confirmLabel={t('planned.cancelSeries')}
          cancelLabel={t('planned.keepSeries')}
          destructive
          busy={confirmBusy}
          error={confirmError}
          onConfirm={() =>
            void runConfirmed(
              () => plannedApi.cancelSeries(cancelSeries.id),
              t('planned.seriesCancelled'),
              () => setCancelSeries(null),
            )
          }
          onCancel={() => setCancelSeries(null)}
        />
      )}

      {subscribe && (
        <Dialog
          open
          onClose={() => setSubscribe(false)}
          maxWidth="xs"
          aria-label={t('subscribe.cardTitle')}
        >
          <DialogContent>
            <SubscribeCard />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export function PlannedPage() {
  return (
    <OwnedEnvelopesProvider>
      <PlannedPageInner />
    </OwnedEnvelopesProvider>
  );
}
